import React, { createContext, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

const AgreementFormsContext = createContext();
const forms = new Map();

export const AgreementFormsProvider = ({ children }) => {
  const [areValidating, setAreValidating] = useState(false);
  const [areSubmitting, setAreSubmitting] = useState(false);

  const registerForm = (id, ref) => {
    if (forms.has(id)) {
      // eslint-disable-next-line no-console
      console.warn(`Agreement Form ${id} already registered.`);
    }

    forms.set(id, ref);
  };

  const unRegisterForm = id => {
    forms.delete(id);
  };

  const resetForms = async () => {
    Object.values(Object.fromEntries(forms)).map(formRef => {
      return formRef.current.resetForm();
    });

    return true;
  };

  const validateForms = async () => {
    setAreValidating(true);
    const results = await Promise.all(
      Object.values(Object.fromEntries(forms)).map(formRef => {
        return formRef.current.validateForm();
      }),
    );
    setAreValidating(false);

    return { success: results.every(v => isEmpty(v)), results };
  };

  const submitForms = async (ignoreDirty = false) => {
    let success = true;
    const validations = await validateForms();

    if (!validations.success) {
      return validations;
    }

    setAreSubmitting(true);
    const results = await Promise.all(
      Object.values(Object.fromEntries(forms)).map(formRef => {
        if (formRef.current.dirty || ignoreDirty) {
          return formRef.current.submitForm().catch(err => {
            success = false;
            return err;
          });
        }
        return false;
      }),
    );
    setAreSubmitting(false);

    // can't get responses until this PR is merged:
    // https://github.com/jaredpalmer/formik/pull/2420
    return { success, results };
  };

  return (
    <AgreementFormsContext.Provider
      value={{
        areValidating, // forms are being validated
        areSubmitting, // forms are in flight
        registerForm, // register a new form
        unRegisterForm, // de-register a form (i.e. when it's removed from the dom)
        validateForms, // validate all forms, without submitting
        submitForms, // submit all forms
        resetForms, // reset all forms
      }}
    >
      {children}
    </AgreementFormsContext.Provider>
  );
};

AgreementFormsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAgreementForms = () => useContext(AgreementFormsContext);
